import { Alert, FormSection } from '@ksyos/design-system';
import { type JSX } from 'react';
import type { CustomControlCode } from '@common/enums';
import type { Section } from '@common/formEngine/formEngine.types';
import { useProcessActivityEditContext } from '@frontend/utils';
import type { EditFormCommonProps } from './editForm.types';
import { EditFormSectionField } from './EditFormSectionField';
import { EditFormSubsection } from './EditFormSubsection';
import { shouldIncludeField, shouldIncludeSubsection } from './editFormUtils';

type EditFormSectionProps<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
> = EditFormCommonProps<Data, ContentKey> & {
    readonly section: Section<Data, ContentKey, TCustomControlCode, Version>;
};

export function EditFormSection<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>({
    contentComponents,
    inputComponents,
    section,
}: EditFormSectionProps<Data, ContentKey, TCustomControlCode, Version>): JSX.Element {
    const { processActivityContextData } = useProcessActivityEditContext<Data, TCustomControlCode, Version>();

    const fields = section.fields
        .filter((field) => shouldIncludeField(field, processActivityContextData))
        .map((field) => (
            <EditFormSectionField
                contentComponents={contentComponents}
                field={field}
                inputComponents={inputComponents}
                key={field.key}
            />
        ));

    const subsections = section.subsections
        ?.filter((subsection) => shouldIncludeSubsection(subsection, processActivityContextData))
        .map((subsection) => (
            <EditFormSubsection
                contentComponents={contentComponents}
                inputComponents={inputComponents}
                key={subsection.fields.map((field) => field.key).join('-')}
                subsection={subsection}
            />
        ));

    return (
        <FormSection
            title={section.title}
            tooltipContent={section.tooltipContent}
        >
            {section.alertText && <Alert appearance="info">{section.alertText}</Alert>}
            {fields}
            {subsections}
        </FormSection>
    );
}
